import { InfoCircleOutlined } from '@ant-design/icons'
import { Button, Modal, Table, Typography } from 'antd'
import {
  COMPONENT_TYPE_INFO,
  MATERIAL_GROUP_META,
} from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsClassificationContent'
import {
  COMPONENT_TYPES,
  ComponentType,
  FIG_4_5_BANDS,
} from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsComponentClassification'
import { componentTypeEligibility } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsRegionRouting'
import { FfsPart } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsTypes'
import { useGeckoLocale } from 'features/locale'
import { FC, ReactNode, useMemo, useState } from 'react'
import styled from 'styled-components'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

const StyledTable = styled(Table)`
  .ant-table {
    font-size: 0.8125rem;
  }
`
const Caption = styled(Typography.Text)`
  display: block;
  margin-top: 0.75rem;
  font-size: 0.75rem;
`

/** An info-link button that opens a read-only reference modal. */
const ReferenceButton: FC<{
  label: string
  title: string
  children: ReactNode
}> = ({ label, title, children }) => {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button
        type='link'
        size='small'
        icon={<InfoCircleOutlined />}
        onClick={() => setOpen(true)}
        style={{ padding: 0 }}>
        {label}
      </Button>
      <Modal
        title={title}
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={720}>
        {children}
      </Modal>
    </>
  )
}

// NPS is a nominal size designation, so its column stays in inches regardless of
// the measurement system. The design-temperature band DOES follow the toggle, so
// its column/rows are built inside the component (below) rather than at module
// scope.

/** Fig 4.5 piping Type-A bands, rendered from `FIG_4_5_BANDS` (single source). */
export const Fig45BandsReference: FC = () => {
  const { getPreferredMeasurementUnit } = useGeckoLocale()
  const tempUnit = getPreferredMeasurementUnit(GeckoMeasurementUnit.Celsius)

  const columns = useMemo(
    () => [
      { title: 'Material group', dataIndex: 'material', key: 'material' },
      { title: 'NPS (in)', dataIndex: 'npsRange', key: 'npsRange' },
      {
        title: `Design temp (${tempUnit.name})`,
        dataIndex: 'tempRange',
        key: 'tempRange',
      },
    ],
    [tempUnit]
  )

  const rows = useMemo(() => {
    // Band bounds are canonical Celsius; display in the preferred temp unit.
    const toTemp = (c: number): number =>
      Math.round(
        GeckoMeasurement.CreateFromValue(
          c,
          GeckoMeasurementUnit.Celsius
        ).toNumber(tempUnit)
      )
    return FIG_4_5_BANDS.map((b, i) => ({
      key: i,
      material: MATERIAL_GROUP_META[b.materialGroup].label,
      npsRange: `${b.npsMinIn}–${b.npsMaxIn}`,
      tempRange: `${toTemp(b.designTempMinC)} … ${toTemp(b.designTempMaxC)}`,
    }))
  }, [tempUnit])

  return (
    <ReferenceButton
      label='View Fig 4.5 piping bands'
      title='API 579-1 Fig 4.5 — Type A piping criteria'>
      <StyledTable
        columns={columns}
        dataSource={rows}
        pagination={false}
        size='small'
      />
      <Caption type='secondary'>
        A straight pipe / elbow / bend with no structural attachment whose NPS
        and design temperature fall within its material-group band may be
        treated as Type A. Source: API 579-1/ASME FFS-1 (2021) Part 4, Fig 4.5 /
        4.5M.
      </Caption>
    </ReferenceButton>
  )
}

const check = (eligible: boolean) => (eligible ? '✓' : '✗')

const ELIGIBILITY_ROWS = COMPONENT_TYPES.map((type) => {
  const e = componentTypeEligibility(type)
  return {
    key: type,
    type,
    p4l1: check(e[FfsPart.PART_4].level1.eligible),
    p4l2: check(e[FfsPart.PART_4].level2.eligible),
    p5l1: check(e[FfsPart.PART_5].level1.eligible),
    p5l2: check(e[FfsPart.PART_5].level2.eligible),
  }
})
const ELIGIBILITY_COLUMNS = [
  { title: 'Type', dataIndex: 'type', key: 'type' },
  { title: 'Part 4 L1', dataIndex: 'p4l1', key: 'p4l1' },
  { title: 'Part 4 L2', dataIndex: 'p4l2', key: 'p4l2' },
  { title: 'Part 5 L1', dataIndex: 'p5l1', key: 'p5l1' },
  { title: 'Part 5 L2', dataIndex: 'p5l2', key: 'p5l2' },
]

/** Type → Part/Level eligibility matrix, from `componentTypeEligibility`. */
export const EligibilityMatrixReference: FC = () => {
  return (
    <ReferenceButton
      label='View eligibility matrix'
      title='Component type → Part / Level eligibility'>
      <StyledTable
        columns={ELIGIBILITY_COLUMNS}
        dataSource={ELIGIBILITY_ROWS}
        pagination={false}
        size='small'
      />
      <Caption type='secondary'>
        This matrix is component-type <em>compatibility</em> — which Part/Level
        assessments a type may ever use. It is not the same as when a part is
        runnable in the workflow: an eligible LTA can be screened under Part 4,
        and Part 5 becomes available for a local LTA directly or once its Part 4
        assessment is complete (Type C is not eligible for either). Source: API
        579-1/ASME FFS-1 (2021) Part 4 §4.2.5 &amp; §4.4.3.3; Part 5 §5.2.5(d)
        &amp; §5.2.6.
      </Caption>
    </ReferenceButton>
  )
}

const TYPE_DEF_ROWS = COMPONENT_TYPES.map((type: ComponentType) => ({
  key: type,
  type,
  ...COMPONENT_TYPE_INFO[type],
}))
const TYPE_DEF_COLUMNS = [
  { title: 'Type', dataIndex: 'type', key: 'type', width: 60 },
  { title: 'Definition', dataIndex: 'title', key: 'title', width: 220 },
  { title: 'Meaning', dataIndex: 'summary', key: 'summary' },
  { title: 'Reference', dataIndex: 'citation', key: 'citation', width: 200 },
]

/** A/B1/B2/C definitions, from `COMPONENT_TYPE_INFO`. */
export const ComponentTypeDefinitionsReference: FC = () => {
  return (
    <ReferenceButton
      label='View component type definitions'
      title='API 579 component types (A / B1 / B2 / C)'>
      <StyledTable
        columns={TYPE_DEF_COLUMNS}
        dataSource={TYPE_DEF_ROWS}
        pagination={false}
        size='small'
      />
    </ReferenceButton>
  )
}
